import type React from "react";
import {Box, Skeleton, Stack, Typography} from "@mui/material";
import {CustomCard} from "~/components/Card/Card";
import {Tag} from "~/components/tag/Tag";
import {COLORS} from "~/style/colors-ts";
import {useRevenueShareQuery} from "~/pages/supplier-360/hooks/useRevenueShareQuery";
import {BAND_COLOR, BAND_LABEL, BAND_LIMITS, formatShare, formatUsdRange, leverageRead} from "~/pages/supplier-360/components/pdl/pdlFormat";

/** The ratio bar's scale ends at 25% — beyond that every value is "critical leverage" anyway. */
const SCALE_MAX_PCT = 25;

function position(pct: number): number {
    return Math.min(100, (pct / SCALE_MAX_PCT) * 100);
}

/** Overview "Commercial leverage": the customer's last-12-months spend as a share of the supplier's revenue, on a four-band bar. Hidden entirely once loaded with no usable revenue figure. */
export const CommercialLeverageCard: React.FC<{entityId: number, supplierName: string}> = ({entityId, supplierName}) => {
    const {data: share, isLoading} = useRevenueShareQuery(entityId);

    if (!isLoading && !share) return null;

    return (
        <CustomCard title="Commercial leverage" extraHeaderContent={<Typography variant="caption">Spend vs supplier revenue</Typography>}
                    sx={{flex: 1, minWidth: 0}}>
            {isLoading && <Skeleton variant="rectangular" height={80}/>}
            {share && (
                <Stack gap={1.5}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight={700}>{formatShare(share)}</Typography>
                        <Tag label={BAND_LABEL[share.band]} color={BAND_COLOR[share.band]} solid={false}/>
                    </Stack>
                    <Box sx={{position: 'relative'}}>
                        <Box sx={{height: 10, display: 'flex', gap: '2px', borderRadius: 1, overflow: 'hidden'}}>
                            {BAND_LIMITS.map(({band, upTo}, i) => {
                                const from = i === 0 ? 0 : BAND_LIMITS[i - 1].upTo ?? 0;
                                const width = position(upTo ?? SCALE_MAX_PCT) - position(from);
                                return (
                                    <Box
                                        key={band}
                                        title={BAND_LABEL[band]}
                                        sx={{width: `${width}%`, bgcolor: band === share.band ? COLORS.info.main : COLORS.grey[150]}}
                                    />
                                );
                            })}
                        </Box>
                        <Box
                            aria-hidden
                            sx={{position: 'absolute', top: -3, left: `calc(${position(share.pct)}% - 1px)`, width: 2, height: 16,
                                bgcolor: COLORS.grey[1000]}}
                        />
                    </Box>
                    <Typography variant="body2">{leverageRead(share, supplierName)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {`Supplier revenue: ${formatUsdRange(share.revenue_min_usd ?? share.revenue_usd, share.revenue_max_usd)}`}
                    </Typography>
                </Stack>
            )}
        </CustomCard>
    );
};
